import { useEffect, useCallback, useMemo, useRef, useState } from 'react'
import classnames from 'classnames'
import Scrollbars from 'react-custom-scrollbars-2'
import SheetHeader from './components/SheetHeader'
import { PlayerStatusList } from './components/PlayerStatusList'
import useGame from '../GameView/hooks/useGame'
import PlayerPopulation from './components/PlayerPopulation/PlayerPopulation'
import PlayerCashInvestments from './components/PlayerCashInvestments'
import PlayerChoosePlanetMovement from './components/PlayerChoosePlanetMovement'
import PlayerTurnMessages from './components/PlayerTurnMessages/PlayerTurnMessages'
import GalacticTrader from './components/GalacticTrader/GalacticTrader'
import './GameSheet.scss'
import { PlayerStarbaseLoans } from './components/PlayerStarbaseLoans'
import { useSelector } from '../../hooks/useSelector'
import { useDispatch } from 'react-redux'
import { pushError, setFullscreen, setMap } from '../../reducers/game/game'
import usePlayerActions from './hooks/usePlayerActions'
import { useFocusPlayer } from '../../hooks/useFocusPlayer'
import { PrivateTradeOffers } from './components/PrivateTradeOffers/PrivateTradeOffers'
import { WaitingOnPlayers } from './components/WaitingOnPlayers/WaitingOnPlayers'
import { useJoinGameRoom } from '../../hooks/useJoinGameRoom'
import { AdminEditingModal } from '../../components/AdminEditingModal'
import { GamePausedModal } from '../../components/GamePausedModal'
import { Box, Stack } from '@prometheus/components'
import { truncateCash } from '@prometheus/utils'
import { GameBoard } from './components/GameBoard'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Icon } from 'semantic-ui-react'
import { useIsMobile } from '../../hooks/useIsMobile'
import { MobilePanelNav, MobilePanel } from './components/MobilePanelNav/MobilePanelNav'
import { MobileTurnStatus } from './components/MobileTurnStatus/MobileTurnStatus'
import { useGameBoardActions } from './hooks/useGameBoardActions'
import { touchDistance, touchMidpoint, pinchWheelSteps } from './pinchZoom'
import { resolveSwipePanel } from './mobileGestures'
import { BoardPlanetSheet } from './components/BoardPlanetSheet/BoardPlanetSheet'
import useReadyGate from './hooks/useReadyGate'
import { ReadyRequirementOverlay } from './components/ReadyRequirementOverlay'
import { BossPanel } from './components/BossPanel/BossPanel'
import { BossAttackModal } from './components/BossPanel/BossAttackModal'
import { useBossPanelHidden } from './hooks/useBossPanelHidden'

export default () => {
  useJoinGameRoom()
  const dispatch = useDispatch()
  const turn = useSelector(s => s.game.metadata?.turn) || 0
  const lastTurn = useRef(turn)
  const adminEditor = useSelector(s => s.game.metadata?.admin_editing)
  const paused = useSelector(s => s.game.metadata?.paused_by)
  const trade = useSelector(s => s.game.metadata?.trade)
  const is_fullscreen = useSelector(s => s.game.fullscreen)
  const is_map = useSelector(s => s.game.galaxy_display)
  const uid = useSelector(s => s.auth.uid) || ''
  const players = useSelector(s => s.game.metadata?.players) || {}
  const players_choosing_planet = useSelector(s => s.game.metadata?.players_choosing_planet) || []
  const cash = useSelector(s => s.game.sheet?.cash)
  const { game } = useGame()
  const { onFocusPlayer } = useFocusPlayer(players[uid])

  // ---------------------------------------------------------------------
  // Phone shell state. Below the mobile cutoff the three desktop columns
  // become four tabbed views (Players / Turn / Finance / Board) — only the
  // ACTIVE view is mounted, inside a natively-scrolling opaque panel. The
  // Board tab unmounts the panel entirely so the Godot iframe receives every
  // touch. (Deliberately no hidden-but-mounted panels and no Scrollbars on
  // phone: both are what made the previous mobile attempt's tabs go blank on
  // iOS — stale zero-height measurements after display:none.)
  // ---------------------------------------------------------------------
  const isMobile = useIsMobile()
  const [mobilePanel, setMobilePanel] = useState<MobilePanel>('turn')
  const { iFrameRef, clearTappedPlanet, bossAttackRequested, clearBossAttackRequest } = useGameBoardActions()
  const { onModifyCashAccounts } = usePlayerActions()
  const boss = useSelector(s => s.game.metadata?.boss)
  // Hidden = the invasion lives in the header chip instead of the column.
  const [bossPanelHidden] = useBossPanelHidden()
  const showBossPanel = !!boss?.alive && !bossPanelHidden

  // The nav cash chip runs the same balance-cash sweep as the SHIP panel's
  // Cash button (deposit surplus into accounts / cover a negative balance),
  // with the same error surfacing as PromiseButtonV2.
  const onCashTap = useCallback(async () => {
    try {
      await onModifyCashAccounts('balance_cash')
    } catch (e) {
      dispatch(pushError({ type: 'error', message: (e as any).message }))
    }
  }, [onModifyCashAccounts, dispatch])

  // The Godot board only zooms via the mouse wheel (no pinch handler in the
  // export), so on touch we drive it with on-screen +/- buttons dispatching
  // synthetic wheel events into the (same-origin) board canvas. deltaY < 0
  // zooms in, > 0 zooms out; two steps per tap feels responsive.
  const onZoomBoard = useCallback((direction: number) => {
    const frame = iFrameRef.current
    const target = frame?.contentDocument?.querySelector('canvas') || frame?.contentWindow
    if (!target) return
    for (let i = 0; i < 2; i++) {
      target.dispatchEvent(new WheelEvent('wheel', { deltaY: direction * 120, bubbles: true, cancelable: true }))
    }
  }, [iFrameRef])

  // Entering the board keeps the camera where it was (the hydrate-default
  // view fills a portrait screen edge-to-edge). Focusing the ship is a
  // deliberate action via the Focus button — auto-focusing here would park
  // the camera at the world's edge and expose the engine's blank margin.
  // Leaving a tab also drops any open board planet sheet.
  const onSelectPanel = useCallback((panel: MobilePanel) => {
    setMobilePanel(panel)
    clearTappedPlanet()
  }, [clearTappedPlanet])

  // Horizontal swipe on the panel switches tabs (same order as the bottom
  // nav). Swipes that start inside a horizontally scrollable element (level
  // tables, wide Tables) scroll that element instead.
  const swipeStartRef = useRef<{ x: number, y: number, target: EventTarget | null } | null>(null)
  const onPanelTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length !== 1) {
      swipeStartRef.current = null
      return
    }
    swipeStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, target: e.target }
  }, [])
  const onPanelTouchEnd = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const start = swipeStartRef.current
    swipeStartRef.current = null
    if (!start || e.changedTouches.length !== 1) return

    let el = start.target as HTMLElement | null
    const boundary = e.currentTarget
    while (el && el !== boundary) {
      if (el.scrollWidth > el.clientWidth + 1) {
        const overflowX = getComputedStyle(el).overflowX
        if (overflowX === 'auto' || overflowX === 'scroll') return
      }
      el = el.parentElement
    }

    const next = resolveSwipePanel(
      mobilePanel,
      e.changedTouches[0].clientX - start.x,
      e.changedTouches[0].clientY - start.y,
    )
    if (next) {
      onSelectPanel(next)
    }
  }, [mobilePanel, onSelectPanel])

  // Floating Ready control: ending a turn shouldn't require reaching the
  // header. Shown while the player still has to ready up (mirrors the
  // header button's enable rules) on panel tabs — the Board tab's corners
  // belong to the zoom/focus controls.
  //
  // The pill goes through the same gate as the header button, so the phone
  // never needs the round trip the desktop can shrug off: a travel choice or
  // a trade answer drops out of the pill itself instead of living on another
  // tab. Tap once for the options, again to let the game choose.
  const readyGate = useReadyGate()

  // Pinch-to-zoom for the board: the Godot export has no pinch handler, so
  // we listen (passively — Godot still sees its touches, no input state can
  // get stuck) inside the same-origin iframe and convert pinch-distance
  // changes into the same synthetic wheel events the +/- buttons dispatch,
  // aimed at the pinch midpoint so the camera zooms toward the fingers.
  useEffect(() => {
    if (!isMobile) return
    const frame = iFrameRef.current
    const doc = frame?.contentDocument
    if (!doc) return

    let lastDist: number | null = null

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        lastDist = touchDistance(e.touches[0], e.touches[1])
      }
    }
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || lastDist === null) return
      const dist = touchDistance(e.touches[0], e.touches[1])
      const { steps, carry } = pinchWheelSteps(lastDist, dist)
      if (steps !== 0) {
        const mid = touchMidpoint(e.touches[0], e.touches[1])
        const target = doc.querySelector('canvas') || frame?.contentWindow
        for (let i = 0; i < Math.abs(steps); i++) {
          target?.dispatchEvent(new WheelEvent('wheel', {
            deltaY: steps > 0 ? -120 : 120, // spreading fingers zooms in
            clientX: mid.x,
            clientY: mid.y,
            bubbles: true,
            cancelable: true,
          }))
        }
        lastDist = dist - carry
      }
    }
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        lastDist = null
      }
    }

    doc.addEventListener('touchstart', onTouchStart, { capture: true, passive: true })
    doc.addEventListener('touchmove', onTouchMove, { capture: true, passive: true })
    doc.addEventListener('touchend', onTouchEnd, { capture: true, passive: true })
    doc.addEventListener('touchcancel', onTouchEnd, { capture: true, passive: true })
    return () => {
      doc.removeEventListener('touchstart', onTouchStart, { capture: true } as any)
      doc.removeEventListener('touchmove', onTouchMove, { capture: true } as any)
      doc.removeEventListener('touchend', onTouchEnd, { capture: true } as any)
      doc.removeEventListener('touchcancel', onTouchEnd, { capture: true } as any)
    }
    // Re-attach when entering the board tab: by then the iframe document
    // definitely exists (it is mounted from first render), and re-running on
    // tab switches also recovers if the iframe reloaded.
  }, [isMobile, mobilePanel, iFrameRef])

  // Pull the player toward the tabs where something is happening: an open
  // trade window or a pending movement choice lives on the Turn tab.
  const isMyMove = players_choosing_planet.includes(uid)
  // The boss's arrival year also lights the Turn tab — that's where its panel
  // (and the one free year to react) lives.
  const bossJustArrived = !!boss?.alive && turn === boss.spawned_turn
  const attention = useMemo(() => ({
    turn: !!trade || isMyMove || bossJustArrived,
  }), [trade, isMyMove, bossJustArrived])

  // One-tap jump to the action: when the game starts waiting on this player
  // (their move, or a trade window) while they're on another panel, a toast
  // above the tab bar jumps straight to the Turn tab. Not shown on the
  // Board tab — picking a planet there IS acting on the move.
  const showTurnToast = isMobile
    && (isMyMove || !!trade)
    && mobilePanel !== 'turn'
    && mobilePanel !== 'board'
  // The pill stays up during a trade now — that used to hide it, because the
  // button had nothing useful to do; the gate gives it one.
  const showReadyFab = isMobile
    && mobilePanel !== 'board'
    && !!players[uid]
    && !players[uid].dead
    && !players[uid].ready

  useEffect(() => {
    if (turn !== lastTurn.current) {
      lastTurn.current = turn
      dispatch(setMap(false))
      dispatch(setFullscreen(false))
    }
  }, [turn, trade, dispatch])

  const onHandleHotkey = useCallback((e: KeyboardEvent) => {
    if (e.keyCode === 70) {
      dispatch(setFullscreen(!is_fullscreen))
    }
    if (e.keyCode === 77) {
      dispatch(setMap(!is_map))
    }
    if (e.keyCode === 32) {
      onFocusPlayer()
    }
  }, [dispatch, is_fullscreen, is_map, onFocusPlayer])

  useEffect(() => {
    window.addEventListener('keyup', onHandleHotkey)
    return () => window.removeEventListener('keyup', onHandleHotkey)
  }, [onHandleHotkey])

  if (game?.status === 'complete') {
    window.location.pathname = '/'
  }

  return (
    <>
      {adminEditor && <AdminEditingModal name={adminEditor} />}
      {paused && <GamePausedModal name={players[paused].name} />}
      {bossAttackRequested && boss?.alive && <BossAttackModal onClose={clearBossAttackRequest} />}
      <div
        className={classnames('pg-game-container', {
          'pg-game-container--mobile': isMobile,
          // On phone panel tabs the board is a passive backdrop — take it out
          // of hit-testing so no touch can be swallowed by the WebGL iframe.
          // On the Board tab it is fully interactive again.
          'pg-board-inert': isMobile && mobilePanel !== 'board',
        })}
      >
        <ErrorBoundary name="SheetHeader">
          <SheetHeader readyGate={readyGate} />
        </ErrorBoundary>
        {isMobile ? (
          <>
            {mobilePanel !== 'board' && (
              <div
                className="pg-mobile-panel"
                onTouchStart={onPanelTouchStart}
                onTouchEnd={onPanelTouchEnd}
              >
                {mobilePanel === 'status' && (
                  <Stack className="pg-mobile-panel__stack">
                    <ErrorBoundary name="PlayerStatusList">
                      <PlayerStatusList />
                    </ErrorBoundary>
                    <ErrorBoundary name="PrivateTradeOffers">
                      <PrivateTradeOffers />
                    </ErrorBoundary>
                    <ErrorBoundary name="PlayerChoosePlanetMovement">
                      <PlayerChoosePlanetMovement owned />
                    </ErrorBoundary>
                  </Stack>
                )}
                {mobilePanel === 'turn' && (
                  <Stack className="pg-mobile-panel__stack">
                    <ErrorBoundary name="MobileTurnStatus">
                      <MobileTurnStatus
                        planetKey={players[uid]?.current_planet}
                        roll={players[uid]?.roll}
                        isMyMove={players_choosing_planet.includes(uid)}
                        tradeOpen={!!trade}
                        onViewBoard={() => onSelectPanel('board')}
                      />
                    </ErrorBoundary>
                    <ErrorBoundary name="WaitingOnPlayers">
                      <WaitingOnPlayers />
                    </ErrorBoundary>
                    {trade && (
                      <ErrorBoundary name="GalacticTrader">
                        <GalacticTrader />
                      </ErrorBoundary>
                    )}
                    {showBossPanel && (
                      <ErrorBoundary name="BossPanel">
                        <BossPanel />
                      </ErrorBoundary>
                    )}
                    <ErrorBoundary name="PlayerChoosePlanetMovement">
                      <PlayerChoosePlanetMovement />
                    </ErrorBoundary>
                  </Stack>
                )}
                {mobilePanel === 'finance' && (
                  <Stack className="pg-mobile-panel__stack">
                    <ErrorBoundary name="PlayerPopulation">
                      <PlayerPopulation />
                    </ErrorBoundary>
                    <ErrorBoundary name="PlayerCashInvestments">
                      <PlayerCashInvestments />
                    </ErrorBoundary>
                    <ErrorBoundary name="PlayerTurnMessages">
                      <PlayerTurnMessages />
                    </ErrorBoundary>
                    <ErrorBoundary name="PlayerStarbaseLoans">
                      <PlayerStarbaseLoans />
                    </ErrorBoundary>
                  </Stack>
                )}
              </div>
            )}
            {mobilePanel === 'board' && (
              <>
                <div className="pg-board-zoom">
                  <button type="button" className="pg-board-zoom__btn" onClick={() => onZoomBoard(-1)} aria-label="Zoom in">
                    <Icon name="plus" />
                  </button>
                  <button type="button" className="pg-board-zoom__btn" onClick={() => onZoomBoard(1)} aria-label="Zoom out">
                    <Icon name="minus" />
                  </button>
                </div>
                <button
                  type="button"
                  className="pg-focus-fab"
                  onClick={onFocusPlayer}
                  aria-label="Center the board on my ship"
                >
                  <Icon name="crosshairs" />
                  Focus
                </button>
                <ErrorBoundary name="BoardPlanetSheet">
                  <BoardPlanetSheet />
                </ErrorBoundary>
              </>
            )}
            {showTurnToast && (
              <button
                type="button"
                className="pg-turn-toast"
                onClick={() => onSelectPanel('turn')}
              >
                <Icon name={trade ? 'exchange' : 'rocket'} />
                {trade ? 'Trade window open — view Turn' : 'Your move — choose a planet'}
              </button>
            )}
            {showReadyFab && readyGate.armed && readyGate.requirement && (
              <ReadyRequirementOverlay
                className="pg-ready-req--fab"
                requirement={readyGate.requirement}
                busy={readyGate.busy}
                onChoose={readyGate.onChooseTravel}
                onAuto={readyGate.onAuto}
                onDismiss={readyGate.onDismiss}
              />
            )}
            {showReadyFab && (
              <button
                type="button"
                className={classnames('pg-ready-fab', { busy: readyGate.busy })}
                onClick={readyGate.onReadyTap}
                disabled={readyGate.busy}
                aria-label={readyGate.requirement
                  ? `Ready up — ${readyGate.requirement.title}`
                  : 'Ready up to end your turn'}
              >
                <Icon name={readyGate.requirement ? 'exclamation' : 'check'} />
                Ready
              </button>
            )}
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', height: '100%', gap: 'var(--prometheus-spacing-base)' }}>
            {is_fullscreen && (
              <div style={{ flex: 1 }}>
                <Scrollbars autoHide style={{ height: '100%' }}>
                  <Box top="sm">
                    <ErrorBoundary name="PlayerChoosePlanetMovement">
                      <PlayerChoosePlanetMovement owned fullscreen />
                    </ErrorBoundary>
                  </Box>
                </Scrollbars>
              </div>
            )}
            {!is_fullscreen && (
              <>
                <div style={{ flexBasis: '320px' }}>
                  <Scrollbars autoHide>
                    <Stack style={{ margin: 'var(--prometheus-spacing-base) 0' }}>
                      <ErrorBoundary name="PlayerStatusList">
                        <PlayerStatusList />
                      </ErrorBoundary>
                      <ErrorBoundary name="PrivateTradeOffers">
                        <PrivateTradeOffers />
                      </ErrorBoundary>
                      <ErrorBoundary name="PlayerChoosePlanetMovement">
                        <PlayerChoosePlanetMovement owned />
                      </ErrorBoundary>
                    </Stack>
                  </Scrollbars>
                </div>
                <div style={{ flex: 1 }}>
                  <Scrollbars
                    autoHide
                  >
                    <Stack style={{ margin: 'var(--prometheus-spacing-base) 0' }}>
                      <ErrorBoundary name="WaitingOnPlayers">
                        <WaitingOnPlayers />
                      </ErrorBoundary>
                      {trade && (
                        <ErrorBoundary name="GalacticTrader">
                          <GalacticTrader />
                        </ErrorBoundary>
                      )}
                      {showBossPanel && (
                        <ErrorBoundary name="BossPanel">
                          <BossPanel />
                        </ErrorBoundary>
                      )}
                      <ErrorBoundary name="PlayerChoosePlanetMovement">
                        <PlayerChoosePlanetMovement />
                      </ErrorBoundary>
                    </Stack>
                  </Scrollbars>
                </div>
              </>
            )}
            <div style={{ flexBasis: '400px' }}>
              <Scrollbars
                autoHide
              >
                <Stack style={{ margin: 'var(--prometheus-spacing-base) 0'}}>
                  <ErrorBoundary name="PlayerPopulation">
                    <PlayerPopulation />
                  </ErrorBoundary>
                  <ErrorBoundary name="PlayerCashInvestments">
                    <PlayerCashInvestments />
                  </ErrorBoundary>
                  <ErrorBoundary name="PlayerTurnMessages">
                    <PlayerTurnMessages />
                  </ErrorBoundary>
                  <ErrorBoundary name="PlayerStarbaseLoans">
                    <PlayerStarbaseLoans />
                  </ErrorBoundary>
                </Stack>
              </Scrollbars>
            </div>
          </div>
        )}
        <GameBoard />
        {isMobile && (
          <MobilePanelNav
            active={mobilePanel}
            onSelect={onSelectPanel}
            attention={attention}
            cashLabel={typeof cash === 'number' ? truncateCash(cash) : undefined}
            onCashTap={onCashTap}
            cashNegative={typeof cash === 'number' && cash < 0}
          />
        )}
      </div>
    </>
  )
}
